import { jobLists } from "character";
import FormComponents from "components/forms/FormComponents";
import Link from "components/link/Link";
import NoCharacter from "components/no-character/NoCharacter";
import { items } from "data-files/items";
import { quests } from "data-files/quests";
import { EorzeaDataType, GameData, InputType, LinkType, RelicData } from "enums";
import manager from "managers/app";
import { ViewRelicInfo } from "managers/types/relic-types";
import { Component } from "react";
import { connect } from "react-redux";
import t from "translations/translator";
import { AppState } from "types/app";
import ContentHeader from "views/page-parts/content/content-header/ContentHeader";
import DetailsSection from "views/page-parts/content/details-section/DetailsSection";
import Obtained from "views/page-parts/content/obtained/Obtained";
import OverviewTable from "views/page-parts/content/overview-table/OverviewTable";
import { Requirement } from "views/page-parts/content/relic-requirements/types";
import { ViewWeaponMandervilleMajesticMandervileProps } from "views/relics/weapons/manderville/majestic-manderville/types";
import { TaskInfo } from "views/type";
import "./WeaponMandervilleMajesticManderville.css";

const mapStateToProps = (state:AppState) => {
	return {
		lang: state.userInfo.options.lang,
		gameLang: state.userInfo.options.gameLang,
		userInfo: state.userInfo,
		hideCompletedSteps: state.userInfo.options.hideCompletedSteps,
		hideCompletedTasks: state.userInfo.options.hideCompletedTasks,
		showJob: state.userInfo.options.job,
	};
};

class WeaponMandervilleMajesticManderville extends Component<ViewWeaponMandervilleMajesticMandervileProps> {
	private requirements:Requirement[] = [];
	private importantNPCs:GameData.NPC[] = [];
	private relicInfo:ViewRelicInfo = {
		jobs: jobLists.relic[RelicData.Group.WEAPON_MANDERVILLE],
		type: RelicData.Type.WEAPON,
		relic: RelicData.Group.WEAPON_MANDERVILLE,
		part: RelicData.Part.WEAPON_MAJESTIC_MANDERVILLE,
		steps: [RelicData.Step.MAJESTIC_MANDERVILLE],
		stepPlaceholders: { [RelicData.Step.MAJESTIC_MANDERVILLE]: { ITEM: GameData.Item.AMPLIFYING_ACHONDRITE } },
		next: null,
		previous: null,
	};

	constructor(props:ViewWeaponMandervilleMajesticMandervileProps){
		super(props);
		this.requirements = this.getRequirements();
		this.importantNPCs = this.getImportantNPCs();
	}

	render():JSX.Element{
		const character = manager.data.getActiveCharacter();
		if(character === null){ return <NoCharacter />; }

		const completeSteps = manager.content.getCompleteSteps(character.progress, this.relicInfo);

		return (
			<div id="weapon-manderville-majestic-manderville" className="relic">
				<ContentHeader requirements={this.requirements} importantNPCs={this.importantNPCs} relicInfo={this.relicInfo}>
					<p>{t.t("Complete")}<Link type={LinkType.LODESTONE} lodestoneType={EorzeaDataType.BLUEQUEST} text={GameData.Quest.A_SPIRITED_REFORGING} lodestoneID={quests[GameData.Quest.A_SPIRITED_REFORGING].lodestoneID} /></p>
					<p>{t.t("This will reward your first relic, subsequent jobs must complete")}<Link type={LinkType.LODESTONE} lodestoneType={EorzeaDataType.BLUEQUEST} text={GameData.Quest.IN_NEED_OF_ADJUSTMENT} lodestoneID={quests[GameData.Quest.IN_NEED_OF_ADJUSTMENT].lodestoneID} /></p>
				</ContentHeader>
				<div id="relic-checklist">
					<OverviewTable character={character} completeSteps={completeSteps} relicInfo={this.relicInfo} />
				</div>
				<div id="relic-details">
					<h1>{t.t("Details")}</h1>
					{this.props.hideCompletedSteps && completeSteps.allComplete ? t.t("All Steps Complete") : null }
					{this.relicInfo.steps.map((step) => {
						let row = "odd-row";

						const taskInfo:TaskInfo[] = [{ task: RelicData.Task.TASK_1, target: manager.relics.getTaskItem(step, RelicData.Task.TASK_1) }];

						return <DetailsSection title={step} placeholders={this.relicInfo.stepPlaceholders[step]} id={step} stepComplete={completeSteps[step]} key={step}>
							<div className="table-wrapper">
								<table className="table">
									<thead><tr><th>{t.t("Obtained")}</th><th>{t.t("Item")}</th><th>{t.t("Quantity / Job")}</th><th>{t.t("Total")}</th><th>{t.t("Used")}</th><th>{t.t("Remaining")}</th><th>{t.t("Inventory")}</th><th>{t.t("To Obtain")}</th></tr></thead>
									<tbody>
										{taskInfo.map((thisTask) => {
											const thisTarget = thisTask.target as GameData.Item;
											const inventory = manager.data.getInventoryStatus({
												item: thisTarget,
												relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, thisTask.task],
											});

											if(this.props.hideCompletedTasks && inventory.toObtain === 0){ return null; }

											const tableRow = <tr key={`${step}-${thisTask.task}`} className={row}>
												<td><Obtained obtained={items[thisTarget].obtained} qtyNeeded={inventory.total} /></td>
												<td>{t.t(thisTarget)}</td>
												<td>{inventory.qtyPerJob.toLocaleString()}</td>
												<td>{inventory.total.toLocaleString()}</td>
												<td>{inventory.used.toLocaleString()}</td>
												<td>{inventory.remaining.toLocaleString()}</td>
												<td><FormComponents.TextInput label={{ hide: true }} input={{ type: InputType.NUMBER, value: inventory.inventory, dataAttributes: { item: thisTarget }, events: { onChange: manager.content.inventoryUpdated }}} /></td>
												<td>{inventory.toObtain.toLocaleString()}</td>
											</tr>;
											row = row === "odd-row" ? "even-row" : "odd-row";
											return tableRow;
										})}
										{
											character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step)
												? <tr className="all-complete"><td colSpan={8}><p>{t.t("All Complete")}</p></td></tr>
												: null
										}
									</tbody>
								</table>
							</div>
						</DetailsSection>;
					})}
				</div>
			</div>
		);
	}

	componentDidMount(){
		manager.view.changeComplete();
	}

	private getRequirements():Requirement[]{
		return [
			{ type: quests[GameData.Quest.GENERATIONAL_BONDING].type, text: GameData.Quest.GENERATIONAL_BONDING, lodestoneID: quests[GameData.Quest.GENERATIONAL_BONDING].lodestoneID },
		];
	}

	private getImportantNPCs():GameData.NPC[]{
		return [GameData.NPC.HILDIBRAND_THAVNAIR, GameData.NPC.GEROLT_RADZ_AT_HAN, GameData.NPC.HOUSE_MANDERVILLE_ARTISAN];
	}
}

export default connect(mapStateToProps)(WeaponMandervilleMajesticManderville);
